import { type SearchableJobSortField } from '@/features/jobs/list/job-sort-fields'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type JobScopeChangeStatus } from '@/features/jobs/job-scope-changes/JobScopeChange'
import { formatMoney } from '@/utils/money-util'
import { getStatusDisplayText } from '@/features/jobs/job-status-display'

/**
 * Unique identifiers for each column. Stored in users' column settings, so
 * existing values must not change.
 */
export type ColumnId = (typeof allColumnIds)[number]

export const allColumnIds = [
  'checkbox',
  'jobTitle',
  'jobId',
  'jobShortId',
  'legendName',
  'workspace',
  'status',
  'progressionStep',
  'jobScopeChangeRequestStatus',
  'clientTeam',
  'clientChampion',
  'clientChampionEmail',
  'proChampion',
  'proChampionEmail',
  'isRequestedPro',
  'payout',
  'total',
  'totalPaid',
  'created',
  'submitted',
  'claimed',
  'completed',
  'advanceAllowed',
  'action',
  'lastProMessageActivity',
  'lastClientMessageActivity',
  'lastRelationshipManagerMessageActivity',
] as const

export interface StaticColumnDetails {
  id: ColumnId
  header: string
  modifyVisibility: boolean
  field?: SearchableJobSortField
  sortable: boolean

  /**
   * Indicates whether the column should be pinned as the first column because it's a checkbox action selector.
   * This makes it immobile in the column manager.
   */
  actionSelector?: boolean
}

export interface MutableColumnDetails {
  id: string
  visible: boolean
}

/**
 * Static column details for populating the Job Management table.
 */
export const JobsTableColumns: StaticColumnDetails[] = [
  {
    id: 'checkbox',
    header: '',
    modifyVisibility: true,
    sortable: false,
    actionSelector: true,
  },
  {
    id: 'jobTitle',
    header: 'Job title',
    modifyVisibility: false,
    field: 'Title',
    sortable: true,
  },
  {
    id: 'jobShortId',
    header: 'Job ID',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: 'legendName',
    header: 'Job type',
    modifyVisibility: true,
    field: 'LegendName',
    sortable: true,
  },
  {
    id: 'workspace',
    header: 'Workspace',
    modifyVisibility: true,
    field: 'WorkspaceName',
    sortable: true,
  },
  {
    id: 'status',
    header: 'Status',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: 'progressionStep',
    header: 'Job progress',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: 'jobScopeChangeRequestStatus',
    header: 'Job scope updates',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: 'clientTeam',
    header: 'Client team',
    modifyVisibility: true,
    field: 'ClientTeamName',
    sortable: true,
  },
  {
    id: 'clientChampion',
    header: 'Client champion',
    modifyVisibility: true,
    field: 'ClientChampionName',
    sortable: true,
  },
  {
    id: 'clientChampionEmail',
    header: 'Client champion email',
    modifyVisibility: true,
    field: 'ClientChampionEmail',
    sortable: true,
  },
  {
    id: 'proChampion',
    header: 'Pro champion',
    modifyVisibility: true,
    field: 'ProChampionName',
    sortable: true,
  },
  {
    id: 'proChampionEmail',
    header: 'Pro champion email',
    modifyVisibility: true,
    field: 'ProChampionEmail',
    sortable: true,
  },
  {
    id: 'isRequestedPro',
    header: 'Is requested pro',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: 'payout',
    header: 'Payout',
    modifyVisibility: true,
    field: 'ProviderCutAmount',
    sortable: true,
  },
  {
    id: 'total',
    header: 'Total',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: 'totalPaid',
    header: 'Total paid',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: 'created',
    header: 'Created',
    modifyVisibility: true,
    field: 'DateCreated',
    sortable: true,
  },
  {
    id: 'submitted',
    header: 'Submitted',
    modifyVisibility: true,
    field: 'DateSubmitted',
    sortable: true,
  },
  {
    id: 'claimed',
    header: 'Claimed',
    modifyVisibility: true,
    field: 'DateClaimed',
    sortable: true,
  },
  {
    id: 'completed',
    header: 'Completed',
    modifyVisibility: true,
    field: 'DateCompleted',
    sortable: true,
  },
  {
    id: 'advanceAllowed',
    header: 'Advance allowed',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: 'lastProMessageActivity',
    header: 'Last pro message',
    modifyVisibility: true,
    field: 'DateLastProMessageActivity',
    sortable: true,
  },
  {
    id: 'lastClientMessageActivity',
    header: 'Last client message',
    modifyVisibility: true,
    field: 'DateLastClientMessageActivity',
    sortable: true,
  },
  {
    id: 'lastRelationshipManagerMessageActivity',
    header: 'Last RM message',
    modifyVisibility: true,
    field: 'DateLastRelationshipManagerMessageActivity',
    sortable: true,
  },
]

/**
 * Returns the exported value for a jobs table column.
 */
export function exportValue(columnId: ColumnId, job: SearchableJob) {
  switch (columnId) {
    case 'jobTitle':
      return job.displayName
    case 'jobId':
      return job.id
    case 'jobShortId':
      return job.shortId
    case 'legendName':
      return job.legendName
    case 'workspace':
      return job.workspace.name
    case 'status':
      return formattedStatus(job)
    case 'progressionStep':
      return formattedProgressionStep(job)
    case 'jobScopeChangeRequestStatus':
      return formattedJobScopeChangeRequestStatus(job.scopeChangeStatus)
    case 'payout':
      return formattedProviderCutAmount(job)
    case 'total':
      return formattedTotalPrice(job)
    case 'clientTeam':
      return job.clientTeamName
    case 'clientChampion':
      return job.championClient?.displayName
    case 'clientChampionEmail':
      return job.championClient?.email ?? undefined
    case 'proChampion':
      return job.championPro?.displayName
    case 'proChampionEmail':
      return job.championPro?.email ?? undefined
    case 'created':
      return formattedDate(job.dateCreated)
    case 'submitted':
      return formattedDate(job.dateSubmitted)
    case 'claimed':
      return formattedDate(job.dateAccepted)
    case 'completed':
      return formattedDate(job.dateClosed)
    case 'advanceAllowed':
      return formattedDate(job.dateAdvanceAllowed)
    case 'lastProMessageActivity':
      return formattedDate(job.dateLastProMessageActivity)
    case 'lastClientMessageActivity':
      return formattedDate(job.dateLastClientMessageActivity)
    case 'lastRelationshipManagerMessageActivity':
      return formattedDate(job.dateLastRelationshipManagerMessageActivity)
    case 'isRequestedPro':
      return formattedBoolean(job.isRequestedPro)
    default:
      return undefined
  }
}

/**
 * Returns the formatted status.
 */
function formattedStatus(job: SearchableJob) {
  const withResolution = () => {
    switch (job.resolution) {
      case 'Cancelled':
        return 'Closed (cancelled)'
      case 'Test':
        return 'Closed (test)'
      default:
        return 'Closed'
    }
  }

  return job.status === 'Closed'
    ? withResolution()
    : getStatusDisplayText(job.status)
}

/**
 * Returns the formatted progression step.
 */
export function formattedProgressionStep(job: SearchableJob) {
  switch (job.progressionStep) {
    case 'OpenItems':
      return 'Open items'
    case 'JobStarted':
      return 'Job started'
    case 'DraftInReview':
      return 'Draft review'
    case 'DraftApproved':
      return 'Draft approved'
    case 'DraftRejected':
      return 'Changes requested'
    case 'AuthorizationRequested':
      return 'Authorization requested'
    case 'AuthorizationSigned':
      return 'Authorization received'
    case 'ReturnFiled':
      return 'Return filed'
    case 'Unspecified':
      return ''
    case 'NotStarted':
      return 'Review documents'
    case 'ExtensionInProgress':
      return 'Extension in progress'
    case 'ExtensionConfirmed':
      return 'Extension confirmed'
    case 'ExtensionAccepted':
      return 'Extension accepted'
    case 'ExtensionRejected':
      return 'Extension rejected'
    case 'ReadyToFile':
      return 'Ready to file'
    case 'ProgressionWaitingOnClient':
      return 'Waiting on client'
    case 'DeliverableCompleted':
      return 'Deliverable completed'
    case 'ReviewCompleted':
      return 'Review completed'
    case 'ExtensionNotStarted':
      return 'Extension not started'
    case 'PreparationCompleted':
      return 'Preparation completed'
    default:
      return ''
  }
}

/**
 * Returns the provider cut formatted in USD.
 */
export function formattedProviderCutAmount(job: SearchableJob) {
  return job.providerCutAmount ? formatMoney(job.providerCutAmount) : ''
}

/**
 * Returns the total price formatted in USD.
 */
export function formattedTotalPrice(job: SearchableJob) {
  return job.total ? formatMoney(job.total) : ''
}

/**
 * Returns who the job is assigned to, followed by its scope items.
 */
export function formattedSecondaryInfo(job: SearchableJob) {
  const otherChampion = job.otherChampion
  const otherChampionText = otherChampion
    ? otherChampion.type === 'Pro'
      ? `Assigned to ${otherChampion.displayName}`
      : otherChampion.displayName
    : null

  return [otherChampionText, job.jobScopeItems.join(' • ')]
    .filter(Boolean)
    .join(' | ')
}

/**
 * Returns a formatted date.
 */
function formattedDate(date: Date | null) {
  return date?.toLocaleString()
}

export function formattedJobScopeChangeRequestStatus(
  scopeChangeRequestStatus: JobScopeChangeStatus,
) {
  switch (scopeChangeRequestStatus) {
    case 'Unspecified':
      return undefined
    case 'Accepted':
      return 'Accepted'
    case 'Rejected':
    case 'ApplyFailed':
      return 'Rejected'
    case 'PendingAdminApproval':
    case 'PendingFinalization':
    case 'PendingClientApproval':
      return 'Pending review'
  }
}

/**
 * Returns a formatted boolean value.
 */
function formattedBoolean(value?: boolean) {
  return value ? 'Yes' : 'No'
}
